import { useNavigate } from 'react-router-dom'
import { Code2, Zap, Trophy } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'

const FEATURED_CHARACTERS = [
  { id: 'dev-lama', name: 'Dev Lama', role: 'Protagonist', color: '#f7c948' },
  { id: 'chad-mercer', name: 'Chad Mercer', role: 'Rival', color: '#60a5fa' },
  { id: 'stacy-queue', name: 'Stacy Queue', role: 'PM / Chaos Agent', color: '#fb7185' },
  { id: 'the-algorithm', name: 'The Algorithm', role: 'Narrator', color: '#4ecdc4' },
]

const HOW_IT_WORKS = [
  {
    icon: Code2,
    title: 'Pick an algorithm',
    body: 'Start with Bubble Sort at Innotek Systems Inc. Progress to Raft consensus at ByteForge.',
  },
  {
    icon: Zap,
    title: 'Play it, don\'t read it',
    body: 'Visualize every swap, compare, and collision. Wrong algorithm choices cause spectacular failures.',
  },
  {
    icon: Trophy,
    title: 'Earn your stars',
    body: 'Race the clock for 3★. Make better choices. Ship the product. Don\'t be Chad.',
  },
]

export default function Landing() {
  const navigate = useNavigate()
  const { user, openLoginModal } = useAuthStore()

  const handleStart = () => {
    if (user) {
      navigate('/games')
    } else {
      openLoginModal()
    }
  }

  return (
    <main className="min-h-screen bg-arcade-bg text-white">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="flex flex-col items-center justify-center text-center px-4 pt-24 pb-20">
        <span className="inline-block px-3 py-1 mb-6 text-xs font-mono font-bold tracking-widest text-arcade-neon border border-arcade-neon/30 rounded-full bg-arcade-neon/5">
          FREE FOR EVERYONE · ALWAYS
        </span>
        <h1 className="text-6xl sm:text-7xl font-mono font-bold tracking-tight mb-4">
          Code
          <span className="text-arcade-neon">Arcade</span>
        </h1>
        <p className="max-w-xl text-lg text-gray-400 mb-8 leading-relaxed">
          Story-driven games that teach you algorithms. Follow Dev Lama from junior dev
          to startup founder — one bad data structure at a time.
        </p>
        <button
          onClick={handleStart}
          className="px-8 py-3.5 bg-arcade-accent hover:bg-violet-500 text-white font-bold text-lg rounded-xl transition-colors shadow-lg shadow-violet-900/40"
        >
          Start Playing — It's Free
        </button>
        <p className="mt-4 text-sm text-gray-600">No credit card. No paywall. No BS.</p>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-4 pb-20">
        <h2 className="text-center text-2xl font-mono font-bold mb-10 text-gray-200">
          How it works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {HOW_IT_WORKS.map((step, i) => (
            <div
              key={i}
              className="bg-arcade-surface border border-arcade-border rounded-xl p-6 flex flex-col gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-arcade-accent/20 flex items-center justify-center">
                <step.icon size={20} className="text-arcade-accent" />
              </div>
              <h3 className="font-bold text-white">{step.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Character strip ──────────────────────────────── */}
      <section className="border-t border-arcade-border bg-arcade-surface/40 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-2xl font-mono font-bold mb-2 text-gray-200">
            Meet the cast
          </h2>
          <p className="text-center text-sm text-gray-500 mb-10">
            AlgoQuest · Chapter 1–6 · Innotek Systems → ByteForge
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {FEATURED_CHARACTERS.map((c) => (
              <div
                key={c.id}
                className="bg-arcade-surface border border-arcade-border rounded-xl p-4 flex flex-col items-center gap-3 text-center"
              >
                {/* Sprite placeholder with colored initial */}
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white relative overflow-hidden"
                  style={{ backgroundColor: c.color + '33', border: `2px solid ${c.color}` }}
                >
                  <span style={{ color: c.color }}>{c.name.charAt(0)}</span>
                  <img
                    src={`/sprites/algoquest/${c.id}/idle.png`}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </div>
                <div>
                  <p className="font-bold text-sm text-white">{c.name}</p>
                  <p className="text-xs text-gray-500">{c.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA footer ───────────────────────────────────── */}
      <section className="text-center px-4 py-20">
        <h2 className="text-3xl font-mono font-bold mb-4 text-white">
          Ready to sort some arrays?
        </h2>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          AlgoQuest is live. Chapter 1 starts at Innotek Systems Inc. It only goes downhill
          from there. In a good way.
        </p>
        <button
          onClick={handleStart}
          className="px-8 py-3.5 bg-arcade-accent hover:bg-violet-500 text-white font-bold rounded-xl transition-colors"
        >
          Play AlgoQuest →
        </button>
        <p className="mt-16 text-xs text-gray-700">
          CodeArcade is a philanthropic learning platform. All content is free for all learners.
          Real product names appear for educational context only.{' '}
          <a href="/legal" className="underline hover:text-gray-500">Legal</a>
        </p>
      </section>
    </main>
  )
}
